interface BaseUrl {
  host: string;
  protocol: string;
  path: string;
}

export type QueryParams = {
    [key: string]: string | number;
};

interface InitOptions {
  baseUrl?: BaseUrl;
  theme?: ThemeMode;
  forceTheme?: ThemeMode;
  hideHeader?: boolean;
  uid?: string;
  lang?: string;
  testIds?: string[];
  root?: HTMLElement;
  customParams?: Record<string, string | number>;
  afterInitCb?: VoidFunction;
}

export type Environment = 'development' | 'production';

export interface Options extends InitOptions {
  environment?: Environment;
}

type ThemeMode = 'light' | 'dark';
type PassiveFeedbackDirection = 'right' | 'left' | 'bottom' | 'top';
type ActiveFeedbackDirection =
    | 'bottom-right'
    | 'bottom-left'
    | 'top-right'
    | 'top-left'
    | 'center';

interface ShowActiveFeedbackOptions {
  onSkip?: () => void;
  onFinish?: () => void;
  onRender?: () => void;
  onClose?: () => void;
  direction?: ActiveFeedbackDirection;
  appearance?: Appearance;
}

interface Survey {
  slug: string;
  events: Array<ShowEvent>;
  owner_id: string;
  uid: string;
  appearance: Appearance | null;
  baseUrl: string;
  fulfilled_events: {
    onload?: boolean;
    scroll?: boolean;
    action?: boolean;
    reachGoal?: boolean;
  };
  isFinished: boolean;
  isRendered: boolean;
  enabled: boolean;
}

interface SurveyDetail {
  survey: Survey | Pick<Survey, 'slug' | 'appearance'>;
}

interface SyncResponse {
  show: boolean;
  error?: string;
}
interface SkipResponse {
  response: 'ok';
}

interface SurveyDetailWithResponse extends SurveyDetail {
  response: SyncResponse | SkipResponse;
}

interface ShowEvent {
  event: 'scroll' | 'onload' | 'action';
  value?: string;
  delayMS?: number;
}

interface Appearance {
    direction?: PassiveFeedbackDirection | ActiveFeedbackDirection;
    closeAfterSeconds?: CloseAfterSeconds;
    isButtonHidden?: boolean;
    isHeightAuto?: boolean;
}

interface CloseAfterSeconds {
    time: number;
}

interface ActiveFeedbackSurvey {
  slug: string;
  events: ShowEvent[];
  appearance: Appearance | null;
}

interface PassiveFeedbackSurvey {
  slug: string;
  showEvents: ShowEvent[];
  targetings: [];
  feedbackType: 'passive';
  noAnnoyExclusion: {
      global: boolean;
      site: boolean;
      survey: boolean;
  };
  appearance: Appearance | null;
}

interface SuitableDetail {
  suitable_surveys: {
      passiveFeedbackSurvey: PassiveFeedbackSurvey | null;
      activeFeedbackSurveys: ActiveFeedbackSurvey[];
  };
}

interface EventCallbackData {
  detail: SurveyDetailWithResponse | SuitableDetail | SurveyDetail;
}

export enum FeedbackEventName {
  RENDERED = 'ya:feedback:survey:rendered',
  SYNC = 'ya:feedback:survey:sync',
  SKIP = 'ya:feedback:survey:skip',
  FINISHED = 'ya:feedback:survey:finished',
  SUITABLE = 'ya:feedback:suitable',
  CLOSE_ACTIVE = 'ya:feedback:active_survey:close',
  SHOW_PASSIVE = 'ya:feedback:passive_survey:show',
  CLOSE_PASSIVE = 'ya:feedback:passive_survey:close',
}

export interface Ya {
  Feedback: {
    App: {
      init(owner_id: string, options?: InitOptions): void,
      setDefaultQueryParams(query: QueryParams): void,
      setActiveFeedbackQueryParams(slug: string, query: QueryParams): void,
      setPassiveFeedbackQueryParams(query: QueryParams): void,
      fetch(): Promise<void>,
      embed(root: HTMLElement): () => void,
      fireEvent: (eventName: string) => void,
      setTheme: (theme: ThemeMode) => void,
      forceTheme: (theme: ThemeMode) => void,
      showActiveFeedback: (slug: string, options?: ShowActiveFeedbackOptions) => void,
      showPassiveFeedback: () => void,
      closePassiveFeedback: () => void,
      subscribe: (eventName: FeedbackEventName, callback: (data: EventCallbackData) => void) => void,
      unsubscribe: (eventName: FeedbackEventName, callback: (data: EventCallbackData) => void) => void,
      disableAllSurveys: () => void,
      enableAllSurveys: () => void,
    }
  }
}
